/**
 * MorningBriefCard — surfaces the Super's Brief (4:30 AM daily email) on
 * Profile so users can (a) preview what tomorrow's email will look like,
 * (b) fire a live test to their inbox on-demand, and (c) see opt-out info.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Badge } from './ui/badge';
import { Sunrise, Mail, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

export const MorningBriefCard = ({ email, tier }) => {
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [open, setOpen] = useState(false);

  const isPaid = tier && tier !== 'free';

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const r = await axios.get(`${API_URL}/api/alerts/morning-brief/preview`, { withCredentials: true });
      setPreviewHtml(r.data?.html || '');
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/alerts/morning-brief/send-now`, {}, { withCredentials: true });
      toast.success(`Super's Brief sent to ${email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Card data-testid="morning-brief-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Sunrise className="w-5 h-5 text-amber-500" />
            Super's Brief · 4:30 AM daily
            {isPaid ? (
              <Badge className="bg-emerald-500 text-white text-[10px]">Active</Badge>
            ) : (
              <Badge variant="outline" className="text-[10px]">Pro only</Badge>
            )}
          </CardTitle>
          <CardDescription>
            8-line morning read — rain, soil temp, dewpoint, dollar-spot pressure, MAD bucket, spray window, GDD.
            The way a golf superintendent walks into the shop at dawn.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!isPaid && (
            <p className="text-xs text-muted-foreground italic">
              The daily brief is a Pro feature. Upgrade to get it delivered before your first coffee.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreview}
              disabled={previewing || !isPaid}
              data-testid="morning-brief-preview-btn"
            >
              {previewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
              Preview
            </Button>
            <Button
              size="sm"
              onClick={handleSend}
              disabled={sending || !isPaid}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="morning-brief-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Send test to {email ? email.split('@')[0] : 'me'}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Fires automatically at 4:30 AM in your local timezone. 20h cooldown prevents duplicates.
          </p>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="morning-brief-preview-dialog">
          <DialogHeader>
            <DialogTitle>Tomorrow's Super Brief · preview</DialogTitle>
            <DialogDescription>
              This is exactly what will land in {email} at 4:30 AM local.
            </DialogDescription>
          </DialogHeader>
          {previewHtml ? (
            <div
              className="rounded-lg border bg-white overflow-hidden"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          ) : (
            <div className="text-sm text-muted-foreground">Loading…</div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MorningBriefCard;
